import BaseApp from "../BaseApp";
import {tryLoadJson} from "../../funcs";
import Api from "../../classes/api/Api";
import RowsStorage from "../../classes/storage/RowsStorage";

const SETTINGS = RowsStorage.get("SETTINGS");

const MENU = RowsStorage.get("MENU");

const now = Date.now();
let index = 0;
const getMenuItemId = function() {
    index++;
    return +(now + "" + index);
};

export default class ReportBgApp extends BaseApp {

    async preload(params, callback) {
        const settings = SETTINGS.collection.get(1);
        const reportsDirName = settings.get("REPORTS_CLIENT");

        if ( reportsDirName && reportsDirName !== "base" ) {
            const customConfig = await tryLoadJson(`js/reports/${reportsDirName}/config.json`);

            this.config = await this.filterReportsByRights(customConfig);
            callback();
        } else {
            const baseConfig = await tryLoadJson("js/reports/config.json");
            this.config = await this.filterReportsByRights(baseConfig);
            callback();
        }
    }

    onReady() {

        const menuChannel = system.apps.channel("Menu");
        const menuReports = this.config.menu || [];

        const reports = this.formatReports(menuReports);
        const formattedMenuReports = this.formatGroups(reports);

        system.hasModule("Voskhod")
            ? menuChannel.addReportItems(formattedMenuReports)
            : menuChannel.addReportItems(reports);

        system.on("readyGridApp", () => {
            const gridChannel = system.apps.channel("Grid");
            const tables = this.config.tables || {};
            const operations = [
                "OPERATION_AUTO",
                "OPERATION_TRAIN",
                "OPERATION_SEA",
                "OPERATION_AIR",
                "OPERATION_REPACKING",
                "OPERATION_CUSTOMS",
                "OPERATION_FORWARD",
                "OPERATION_DOCUMENTATION",
                "OPERATION_OTHER"
            ];
            let reportItems;

            for (const tableName in tables) {
                reportItems = tables[tableName];

                gridChannel.setReportItems(tableName, reportItems);

                if ( tableName === "OPERATION" ) {
                    operations.forEach((operation) => {
                        gridChannel.setReportItems(operation, reportItems);
                    });
                }
            }
        });
    }

    /**
     * Производит фильтрацию полученного конфига отчетов согласно правам текущего пользователя
     * @param {Config} customConfig - конфиг полученный из файла конфига отчетов
     * @returns {Promise<Config>} config - отфильтрованный
     */
    async filterReportsByRights(customConfig) {
        const customConfigTables = customConfig?.tables ?? {};
        const availableSectionsAndReports = await this._getAvailableSectionsAndReports();

        const {
            result: { sectionMap }
        } = await Api.request({
            root: "customlogic",
            entity: "reports",
            method: "getReportConfig"
        });
        const sectionsNames = Object.keys(sectionMap || {});

        const tableReportsMap = {};

        for (let i = 0; i < sectionsNames.length; i++) {
            const sectionName = sectionsNames[i];
            const sectionTables = sectionMap[sectionName];
            const sectionTablesNames = sectionTables.map((table) => table.name);
            const availableSectionAndReports = availableSectionsAndReports.find((availableSectionAndReports) => {
                return availableSectionAndReports.section === sectionName;
            });
            const availableSectionReports = availableSectionAndReports?.reports;

            if ( !availableSectionReports?.length ) {
                continue;
            }

            for (let j = 0; j < sectionTablesNames.length; j++) {
                const tableName = sectionTablesNames[j];
                const customConfigTable = customConfigTables[tableName];
                const sectionTable = sectionTables.find((table) => table.name === tableName);
                const specificSectionTable = sectionTables.find((table) => !table.filter);
                const genericSectionTable = sectionTables.find((table) => !!table.filter);

                const reports = customConfigTable?.length
                    ? this.filterCustomConfigTableReports(
                        customConfigTable,
                        availableSectionReports
                    )
                    : [];

                if ( !reports.length ) {
                    continue;
                }

                tableReportsMap[tableName] = tableReportsMap[tableName]
                    ? tableReportsMap[tableName].concat(reports)
                    : reports;

                // Если для таблицы секции указан фильтр, значит скорее всего есть специализированная таблица секции
                // Которой можно так же добавить этот отчет
                if ( sectionTable.filter && specificSectionTable ) {
                    const specificSectionTableName = specificSectionTable.name;

                    tableReportsMap[specificSectionTableName] = tableReportsMap[specificSectionTableName]
                        ? tableReportsMap[specificSectionTableName].concat(reports)
                        : reports;
                }

                // отчеты из специализированной таблицы секции необходимо добавить в общей таблицы секции
                if ( !sectionTable.filter && genericSectionTable ) {
                    const genericSectionTableName = genericSectionTable.name;

                    // естественно что бы в общей таблицы не были видны отчеты все подряд
                    // им нужны фильтры видимости
                    const reportsWithToggleBy = reports.map((report) => {
                        if ( report.toggleBy ) {
                            return report;
                        }

                        report.toggleBy = {
                            COLUMN_FILTER: {
                                key: genericSectionTable.filter.name,
                                type: "equal",
                                value: genericSectionTable.filter.value
                            }
                        };

                        return report;
                    });

                    tableReportsMap[genericSectionTableName] = tableReportsMap[genericSectionTableName]
                        ? tableReportsMap[genericSectionTableName].concat(reportsWithToggleBy)
                        : reportsWithToggleBy;
                }
            }
        }

        const tablesNames = Object.keys(customConfigTables);

        for (let i = 0; i < tablesNames.length; i++) {
            const tableName = tablesNames[i];
            tableReportsMap[tableName] = this.clearFromDuplicatesTableReports(tableReportsMap[tableName]);
        }

        customConfig.tables = tableReportsMap;

        return customConfig;
    }

    async _getAvailableSectionsAndReports() {
        const response = await Api.request({
            root: "customlogic",
            method: "getAvailableSectionsAndReports",
            entity: "reports"
        });

        const availableSectionsAndReports = response.status === "success" ? response.result : [];

        return availableSectionsAndReports;
    }

    /**
     * Отфильтровывает доступные отчеты по таблице
     * @param {AvailableReports[]} availableReports
     * @param {ConfigTable[]} customConfigTableReports - настройки отчетов таблицы (из файла конфига)
     * @returns {ConfigTable[]} Отфильтрованный конфиг отчетов таблицы
     */
    filterCustomConfigTableReports(customConfigTableReports, availableReports) {
        const filtredCustomConfigTableReports = customConfigTableReports.filter((customConfigTableReport) => {
            return availableReports.some((availableReport) => {
                return availableReport === customConfigTableReport.report;
            });
        });

        return filtredCustomConfigTableReports;
    }

    clearFromDuplicatesTableReports(tableReports) {
        if ( !tableReports ) {
            return;
        }

        const clearTableReports = [];

        tableReports.forEach((tableReport) => {
            const originTableReport = clearTableReports.find(
                (clearTableReport) => clearTableReport.report === tableReport.report
            );

            if ( !originTableReport ) {
                clearTableReports.push(tableReport);
                return;
            }

            if ( originTableReport.toggleBy ) {
                const indexOriginTableReport = clearTableReports.indexOf(originTableReport);

                clearTableReports[indexOriginTableReport] = tableReport;
            }

            return;
        });

        return clearTableReports;
    }

    formatReports(menuReports) {
        return menuReports.map((item) => {
            return system.hasModule("Voskhod")
                ? {
                    name: item.title,
                    action: "app",
                    id: getMenuItemId(),
                    type: "StimulReport",
                    report: item.report,
                    group: item.group,
                    sql_alias: item.sql_alias,
                    sql_column: item.sql_column
                }
                : {
                    name: item.title,
                    action: "app",
                    id: getMenuItemId(),
                    data: {
                        type: "StimulReport",
                        report: item.report,
                        sql_alias: item.sql_alias,
                        sql_column: item.sql_column
                    }
                };
        });
    }

    formatGroups(reports) {
        const menuItems = MENU.collection.models;
        const reportParent = menuItems.find((item) => item.get("ICON") === "pie-chart");
        // lw-10434
        // в базе может не быть пункта с отчетами
        if ( !reportParent ) {
            return [];
        }

        const groups = menuItems.filter((item) => {
            return item.get("ID_PARENT") == reportParent.id;
        });

        const repsByGroup = {};
        const repsWithoutGroup = [];
        for (const report of reports) {
            if ( report.group ) {
                const groupName = report.group;
                const reportsInGroup = repsByGroup[groupName] || [];
                repsByGroup[groupName] = reportsInGroup;

                reportsInGroup.push(report);
            } else {
                report._parentId = reportParent.id;
                repsWithoutGroup.push(report);
            }
        }

        const formattedGroups = groups.map((group) => {
            const links = repsByGroup[group.get("NAME")];

            if ( links ) {
                links.forEach((rep) => {
                    rep._parentId = group.id;
                });
            }

            const app = group.get("APP");
            if ( app ) {
                return {
                    app,
                    action: "app",
                    name: group.get("NAME"),
                    _parentId: group.get("ID_PARENT"),
                    id: group.id
                };
            }

            return {
                action: "subMenu",
                name: group.get("NAME"),
                _parentId: group.get("ID_PARENT"),
                id: group.id,
                subMenu: links
            };
        });

        return [...formattedGroups, ...repsWithoutGroup];
    }
}
